import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, X, RefreshCw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const empty = () => ({ fornecedor: "", categoria: "Despesas Administrativas", descricao: "", valor: 0, vencimento: new Date().toISOString().slice(0, 10), status: "pendente", recorrencia: "nenhuma", recorrencia_repeticoes: 12 });
const statusColors = {
  pendente: "bg-amber-100 text-amber-700 border-amber-200",
  parcial: "bg-blue-100 text-blue-700 border-blue-200",
  pago: "bg-emerald-100 text-emerald-700 border-emerald-200",
  atrasado: "bg-red-100 text-red-700 border-red-200",
};
const recorrenciaLabel = { semanal: "Semanal", quinzenal: "Quinzenal", mensal: "Mensal", mensal_indeterminado: "Mensal (indeterminado)" };
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function Payables() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [showPaid, setShowPaid] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [payOpen, setPayOpen] = useState(false);
  const [paying, setPaying] = useState(null);
  const [payForm, setPayForm] = useState({ valor: 0, data_pagamento: new Date().toISOString().slice(0, 10) });

  const load = () => {
    const params = {};
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    api.get("/payables", { params }).then(r => setItems(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  const filteredItems = showPaid ? items : items.filter(p => p.status !== "pago");
  const paidCount = items.filter(p => p.status === "pago").length;
  const clearFilters = () => { setFrom(""); setTo(""); };

  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty(), ...p, recorrencia: "nenhuma" }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, valor: parseFloat(form.valor) };
      if (payload.recorrencia === "nenhuma") { payload.recorrencia = null; payload.recorrencia_repeticoes = null; }
      else if (payload.recorrencia !== "mensal_indeterminado") payload.recorrencia_repeticoes = parseInt(payload.recorrencia_repeticoes) || 1;
      if (editing) await api.put(`/payables/${editing.id}`, payload);
      else await api.post("/payables", payload);
      toast.success(editing ? "Atualizado" : "Criado");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const startPay = (p) => {
    setPaying(p);
    setPayForm({ valor: round2(p.valor - (p.valor_pago || 0)), data_pagamento: new Date().toISOString().slice(0, 10) });
    setPayOpen(true);
  };
  const submitPay = async () => {
    try {
      const { data } = await api.post(`/payables/${paying.id}/pay`, { data_pagamento: payForm.data_pagamento, valor: parseFloat(payForm.valor) });
      toast.success(data.status === "pago" ? "Conta paga integralmente" : `Pagamento parcial registrado — saldo restante ${fmtBRL(data.saldo_restante)}`);
      setPayOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir conta ${p.fornecedor}?`)) return;
    try { await api.delete(`/payables/${p.id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const hasFilters = from || to;

  return (
    <Layout title="Contas a Pagar">
      <Card className="p-4 rounded-sm mb-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div className="flex items-end gap-3 flex-wrap">
            <div><Label className="text-xs font-semibold">Vencimento de</Label><Input data-testid="payable-filter-from" type="date" className="w-40 mt-1" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
            <div><Label className="text-xs font-semibold">Vencimento até</Label><Input data-testid="payable-filter-to" type="date" className="w-40 mt-1" value={to} onChange={(e) => setTo(e.target.value)} /></div>
            {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="payable-filter-clear"><X className="w-4 h-4 mr-1"/>Limpar</Button>}
            <label className="flex items-center gap-2 text-xs pb-1.5" data-testid="payable-show-paid-wrap">
              <Switch checked={showPaid} onCheckedChange={setShowPaid} data-testid="payable-show-paid" />
              Mostrar contas já pagas
              {paidCount > 0 && <span className="text-muted-foreground">({paidCount})</span>}
            </label>
          </div>
          <Button data-testid="payable-new" onClick={openNew}><Plus className="w-4 h-4 mr-1"/>Nova Conta</Button>
        </div>
      </Card>
      <Card className="rounded-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-secondary/60"><TableRow><TableHead>Fornecedor</TableHead><TableHead>Categoria</TableHead><TableHead>Vencimento</TableHead><TableHead>Valor</TableHead><TableHead>Pago</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead></TableRow></TableHeader>
          <TableBody>{filteredItems.map(p => (
            <TableRow key={p.id} data-testid={`payable-row-${p.id}`}>
              <TableCell className="font-medium">
                {p.fornecedor}
                {p.recurrence_type && (
                  <div className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                    <RefreshCw className="w-2.5 h-2.5"/>
                    {recorrenciaLabel[p.recurrence_type] || p.recurrence_type}
                    {p.recurrence_total ? ` (${p.recurrence_index}/${p.recurrence_total})` : ""}
                  </div>
                )}
              </TableCell>
              <TableCell>{p.categoria}</TableCell>
              <TableCell>{fmtDate(p.vencimento)}</TableCell><TableCell>{fmtBRL(p.valor)}</TableCell>
              <TableCell className="text-xs">{p.valor_pago ? fmtBRL(p.valor_pago) : "—"}</TableCell>
              <TableCell><Badge variant="outline" className={`${statusColors[p.status]} capitalize text-[10px]`}>{p.status}</Badge></TableCell>
              <TableCell className="text-right space-x-1">
                {p.status !== "pago" && <Button data-testid={`payable-pay-${p.id}`} size="sm" variant="outline" onClick={() => startPay(p)}>Pagar</Button>}
                <Button data-testid={`payable-edit-${p.id}`} variant="ghost" size="sm" onClick={() => openEdit(p)}><Pencil className="w-4 h-4"/></Button>
                <Button data-testid={`payable-delete-${p.id}`} variant="ghost" size="sm" onClick={() => remove(p)}><Trash2 className="w-4 h-4"/></Button>
              </TableCell>
            </TableRow>
          ))}{filteredItems.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">{showPaid ? "Sem contas" : "Sem contas pendentes"}</TableCell></TableRow>}</TableBody>
        </Table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar Conta" : "Nova Conta a Pagar"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Fornecedor</Label><Input data-testid="payable-fornecedor" value={form.fornecedor} onChange={(e) => setForm({...form, fornecedor: e.target.value})} className="mt-1"/></div>
            <div><Label className="text-xs font-semibold">Categoria</Label><Input value={form.categoria} onChange={(e) => setForm({...form, categoria: e.target.value})} className="mt-1"/></div>
            <div><Label className="text-xs font-semibold">Descrição</Label><Input value={form.descricao || ""} onChange={(e) => setForm({...form, descricao: e.target.value})} className="mt-1"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Valor</Label><Input data-testid="payable-valor" type="number" value={form.valor} onChange={(e) => setForm({...form, valor: e.target.value})} className="mt-1"/></div>
              <div><Label className="text-xs font-semibold">Vencimento</Label><Input type="date" value={form.vencimento} onChange={(e) => setForm({...form, vencimento: e.target.value})} className="mt-1"/></div>
            </div>
            {!editing && (
              <div className="grid grid-cols-2 gap-3 p-3 border border-dashed border-border rounded-sm bg-muted/10">
                <div>
                  <Label className="text-xs font-semibold">Recorrência</Label>
                  <Select value={form.recorrencia} onValueChange={(v) => setForm({...form, recorrencia: v})}>
                    <SelectTrigger data-testid="payable-recorrencia" className="mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nenhuma">Nenhuma</SelectItem>
                      <SelectItem value="semanal">Semanal (nº de repetições)</SelectItem>
                      <SelectItem value="quinzenal">Quinzenal (nº de repetições)</SelectItem>
                      <SelectItem value="mensal">Mensal (nº de repetições)</SelectItem>
                      <SelectItem value="mensal_indeterminado">Mensal por tempo indeterminado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {["semanal", "quinzenal", "mensal"].includes(form.recorrencia) && (
                  <div>
                    <Label className="text-xs font-semibold">Repetições</Label>
                    <Input data-testid="payable-recorrencia-repeticoes" type="number" min="2" max="120" value={form.recorrencia_repeticoes} onChange={(e) => setForm({...form, recorrencia_repeticoes: e.target.value})} className="mt-1"/>
                  </div>
                )}
                {form.recorrencia === "mensal_indeterminado" && (
                  <p className="col-span-2 text-[10px] text-muted-foreground">Uma nova conta do próximo mês é criada automaticamente sempre que a atual for paga integralmente.</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={save} data-testid="payable-save">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Pagar Conta</DialogTitle></DialogHeader>
          {paying && (<div className="space-y-3">
            <div className="text-sm text-muted-foreground">{paying.fornecedor} • Total {fmtBRL(paying.valor)} • Já pago {fmtBRL(paying.valor_pago || 0)}</div>
            <p className="text-[10px] text-muted-foreground">Você pode pagar o valor total ou um valor parcial — o saldo restante fica pendente.</p>
            <div><Label className="text-xs font-semibold">Valor a pagar</Label><Input data-testid="payable-pay-valor" type="number" step="0.01" value={payForm.valor} onChange={(e) => setPayForm({...payForm, valor: e.target.value})} className="mt-1"/></div>
            <div><Label className="text-xs font-semibold">Data do pagamento</Label><Input type="date" value={payForm.data_pagamento} onChange={(e) => setPayForm({...payForm, data_pagamento: e.target.value})} className="mt-1"/></div>
          </div>)}
          <DialogFooter><Button onClick={submitPay} data-testid="payable-pay-confirm">Confirmar Pagamento</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
